import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Descriptions, Input, Modal, Table, Tag } from "antd";
import Cookies from "js-cookie";
import {
  propertyOwnershipLabel,
  applicantPropertyCpfUsage,
  totalPropertyCpfUsage,
} from "../../utils/propertyLoanOwnership";
import {
  propertyLoanName,
  propertyLoanTenureLabel,
  halfCaveatLimit,
} from "../../utils/propertyLoanProducts";
import {
  PROPERTY_MARKETPLACE_STATES,
  propertyMarketplaceStatus,
  propertyMarketplaceLabel,
} from "../../utils/propertyLoanMarketplace";
import PropertyLoanRepaymentSchedule from "../borrower-dashboard/PropertyLoanRepaymentSchedule";
import "./PropertyApplications.css";
import PropertyApplicationComments from "../../components/PropertyApplicationComments";
import PropertyCoOwnerInvitations from "./PropertyCoOwnerInvitations";

const API = "https://www.smart-lend.com/api/support/property-applications";
const DOCUMENT_CATEGORIES = [
  ["noa", "NOA"],
  ["cpf_property_statement", "CPF Property Statement"],
  ["loan_statement", "Loan Statement"],
  ["bank_statement", "Bank Statement"],
  ["other", "Additional documents"],
];
const documentCategoryLabel = (category) =>
  DOCUMENT_CATEGORIES.find(([key]) => key === category)?.[1] ||
  "Additional document";
const money = (value) =>
  Number(value || 0).toLocaleString("en-SG", {
    style: "currency",
    currency: "SGD",
  });
const date = (value) =>
  value
    ? new Date(value).toLocaleString("en-SG", { timeZone: "Asia/Singapore" })
    : "—";
const formFor = (row) => {
  if (typeof row?.loan_data === "string") {
    try {
      return JSON.parse(row.loan_data) || {};
    } catch {
      return {};
    }
  }
  return row?.loan_data || {};
};
const address = (row) => {
  const form = formFor(row);
  const a = form.property?.address || {};
  return (
    [
      a.block?.value,
      a.street?.value,
      a.floor?.value && a.unit?.value
        ? `#${a.floor.value}-${a.unit.value}`
        : "",
      a.postal?.value,
    ]
      .filter(Boolean)
      .join(", ") ||
    form.addressShownData ||
    "Not supplied"
  );
};
const headers = () => ({
  Authorization: `Bearer ${Cookies.get("support_token") || ""}`,
  Accept: "application/json",
  "Content-Type": "application/json",
});
function Status({ row }) {
  return (
    <Tag
      color={PROPERTY_MARKETPLACE_STATES[propertyMarketplaceStatus(row)].color}
    >
      {propertyMarketplaceLabel(row)}
    </Tag>
  );
}
function documentUrl(value) {
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

export default function PropertyApplications({ onOpenBorrower }) {
  const [data, setData] = useState({ data: [], total: 0 });
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contentAction, setContentAction] = useState("");
  const [commentDraft, setCommentDraft] = useState("");
  const [editingComment, setEditingComment] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [uploadFiles, setUploadFiles] = useState({});
  const [contentNotice, setContentNotice] = useState("");
  const fileInputs = useRef({});
  const busy = saving || !!contentAction;
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selected, setSelected] = useState(null);
  const requestId = useRef(0);
  const mounted = useRef(true);
  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const response = await fetch(
        `${API}?${new URLSearchParams({ status, search, page })}`,
        { headers: headers() },
      );
      const body = await response.json();
      if (!response.ok)
        throw new Error(
          body.message || "Unable to load submitted property applications.",
        );
      if (id === requestId.current && mounted.current) {
        setData(body);
        setSelected((current) =>
          current
            ? body.data.find((row) => row.id === current.id) || current
            : null,
        );
        setError("");
      }
    } catch (err) {
      if (id === requestId.current && mounted.current)
        setError(
          err.message || "Unable to load property applications. Please retry.",
        );
    } finally {
      if (id === requestId.current && mounted.current) setLoading(false);
    }
  }, [status, search, page]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      ++requestId.current;
    };
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setCommentDraft("");
    setEditingComment(null);
    setEditDraft("");
    setUploadFiles({});
    setContentNotice("");
  }, [selected?.id]);

  const saveContent = async (kind, category = "other") => {
    if (!selected || busy) return;
    const files = uploadFiles[category] || [];
    if (
      kind === "documents" &&
      (!files.length ||
        files.length > 10 ||
        files.some((file) => file.size > 50 * 1024 * 1024))
    ) {
      setError("Choose up to 10 files, no larger than 50 MB each.");
      return;
    }
    if (
      kind === "documents" &&
      category === "bank_statement" &&
      files.some((file) => !/\.(pdf|csv|xls|xlsx|zip)$/i.test(file.name))
    ) {
      setError("Bank statements must be PDF, CSV, Excel or ZIP files.");
      return;
    }
    if (kind === "comments" && !commentDraft.trim()) return;
    ++requestId.current;
    setLoading(false);
    setContentAction(kind === "documents" ? `documents:${category}` : kind);
    setError("");
    setContentNotice("");
    const requestHeaders = headers();
    let body;
    if (kind === "documents") {
      body = new FormData();
      body.append("category", category);
      files.forEach((file) => body.append("documents[]", file));
      delete requestHeaders["Content-Type"];
    } else body = JSON.stringify({ comment: commentDraft.trim() });
    try {
      const response = await fetch(`${API}/${selected.id}/${kind}`, {
        method: "POST",
        headers: requestHeaders,
        body,
      });
      const result = await response.json();
      if (!mounted.current) return;
      if (!response.ok)
        throw new Error(result.message || "Unable to save. Please retry.");
      setSelected(result.application);
      setData((current) => ({
        ...current,
        data: current.data.map((row) =>
          row.id === result.application.id ? result.application : row,
        ),
      }));
      if (kind === "comments") setCommentDraft("");
      else {
        setUploadFiles((current) => ({ ...current, [category]: [] }));
        if (fileInputs.current[category])
          fileInputs.current[category].value = "";
      }
      setContentNotice(result.message || "Saved successfully.");
    } catch (err) {
      if (mounted.current)
        setError(err.message || "Unable to save. Please retry.");
    } finally {
      if (mounted.current) setContentAction("");
    }
  };

  const changeContent = async (kind, id, method) => {
    if (!selected || busy) return;
    if (method === "PATCH" && !editDraft.trim()) return;
    ++requestId.current;
    setLoading(false);
    setContentAction(`${method}:${kind}:${id}`);
    setError("");
    setContentNotice("");
    try {
      const response = await fetch(
        `${API}/${selected.id}/${kind}/${encodeURIComponent(id)}`,
        {
          method,
          headers: headers(),
          ...(method === "PATCH"
            ? { body: JSON.stringify({ comment: editDraft.trim() }) }
            : {}),
        },
      );
      const result = await response.json();
      if (!mounted.current) return;
      if (!response.ok)
        throw new Error(
          result.message || "Unable to save changes. Please retry.",
        );
      setSelected(result.application);
      setData((current) => ({
        ...current,
        data: current.data.map((row) =>
          row.id === result.application.id ? result.application : row,
        ),
      }));
      if (kind === "comments" && editingComment === id) {
        setEditingComment(null);
        setEditDraft("");
      }
      setContentNotice(result.message || "Changes saved.");
    } catch (err) {
      if (mounted.current)
        setError(err.message || "Unable to save changes. Please retry.");
    } finally {
      if (mounted.current) setContentAction("");
    }
  };

  const changeVisibility = async (nextStatus) => {
    if (!selected || busy) return;
    const application = selected;
    ++requestId.current;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const response = await fetch(`${API}/${application.id}/marketplace`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({
          status: nextStatus,
          expected_status: propertyMarketplaceStatus(application),
        }),
      });
      const body = await response.json();
      if (!mounted.current) return;
      if (body.application) setSelected(body.application);
      if (!response.ok) {
        if (response.status === 409) await load();
        throw new Error(
          body.message || "Unable to change marketplace visibility.",
        );
      }
      setSelected(null);
      await load();
      if (mounted.current)
        setNotice(body.message || "Marketplace visibility updated.");
    } catch (err) {
      if (mounted.current)
        setError(
          err.message ||
            "Unable to change marketplace visibility. Please retry.",
        );
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  const loan = formFor(selected);
  const coOwners = Array.isArray(loan.otherOwners) ? loan.otherOwners : [];
  const documents = [
    ...(loan.cbs_report_document
      ? [{ ...loan.cbs_report_document, category: "CBS report" }]
      : []),
    ...(loan.documentForCpfUsage || []).map((item) => ({
      ...item,
      category: "CPF statement",
    })),
    ...(loan.support_documents || []).map((item) => ({
      ...item,
      category: documentCategoryLabel(item.category),
      removable: true,
    })),
    ...(loan.documentForLoanOustanding || []).map((item) => ({
      ...item,
      category: "Mortgage statement",
    })),
  ];
  const currentStatus = propertyMarketplaceStatus(selected);
  const detailRows = [
    ["Property", address(selected)],
    [
      "Property ownership",
      propertyOwnershipLabel(loan.propertyOwnership) || "—",
    ],
    ...(loan.propertyOwnership === "TenancyInCommon"
      ? [["Applicant's share", `${loan.propertyShare}%`]]
      : []),
    [
      "Property valuation",
      money(loan.estimatedPropertyValue ?? loan.valuation),
    ],
    ["Outstanding mortgage", money(loan.oustandingLoan)],
    ["Applicant CPF housing usage", money(applicantPropertyCpfUsage(loan))],
    ["Total CPF housing usage", money(totalPropertyCpfUsage(loan))],
    ["Loan product", propertyLoanName(loan.selectedLoanType)],
    ["Requested amount", money(loan.loanAmount)],
    ...(Number(loan.selectedLoanType) === 2
      ? [
          [
            "Half Caveat borrowing limit",
            money(halfCaveatLimit(loan, loan.estimatedPropertyValue)),
          ],
        ]
      : []),
    ["Tenure", propertyLoanTenureLabel(loan.selectedLoanType, loan)],
    [
      "Repayment type",
      loan.loanPaymentType === "interest"
        ? "Interest only; principal at maturity"
        : "Principal + interest",
    ],
    ["Submitted", date(selected?.created_at)],
    ["Visibility last changed", date(loan.marketplace?.updated_at)],
  ];

  return (
    <section className="support-property-applications">
      <h2>Property Applications</h2>
      <p>
        Review completed submissions before publishing them. Pending and
        withdrawn applications are hidden from the lender marketplace.
      </p>
      <div className="support-property-toolbar">
        <label>
          Marketplace status{" "}
          <select
            aria-label="Marketplace status"
            value={status}
            disabled={busy}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
              setSelected(null);
            }}
          >
            <option value="all">All submitted applications</option>
            {Object.entries(PROPERTY_MARKETPLACE_STATES).map(
              ([value, state]) => (
                <option key={value} value={value}>
                  {state.label}
                </option>
              ),
            )}
          </select>
        </label>
        <Input.Search
          aria-label="Search property applications"
          placeholder="Borrower name, email, phone or application ID"
          maxLength={200}
          allowClear
          disabled={busy}
          onSearch={(value) => {
            setSearch(value.trim());
            setPage(1);
            setSelected(null);
          }}
        />
        <Button onClick={load} loading={loading} disabled={busy}>
          Refresh applications
        </Button>
      </div>
      {error && <Alert role="alert" type="error" message={error} showIcon />}
      {notice && (
        <Alert role="status" type="success" message={notice} showIcon />
      )}
      <Table
        rowKey="id"
        loading={loading}
        dataSource={data.data}
        scroll={{ x: 1100 }}
        pagination={{
          current: page,
          total: data.total,
          pageSize: 25,
          showSizeChanger: false,
          onChange: setPage,
        }}
        locale={{ emptyText: "No submitted property applications found." }}
        columns={[
          { title: "Application", key: "id", render: (_, row) => `#${row.id}` },
          {
            title: "Borrower / contact",
            key: "borrower",
            render: (_, row) => (
              <div>
                <b>
                  {row.borrower?.principal_name || `Borrower ${row.user_id}`}
                </b>
                <div>
                  {row.borrower?.mobile_no ||
                    row.borrower?.company_contact ||
                    "—"}
                </div>
                <div>
                  {row.borrower?.email || row.borrower?.company_email || "—"}
                </div>
              </div>
            ),
          },
          {
            title: "Property",
            key: "property",
            render: (_, row) => address(row),
          },
          {
            title: "Requested amount",
            key: "amount",
            render: (_, row) => money(formFor(row).loanAmount),
          },
          { title: "Submitted", dataIndex: "created_at", render: date },
          {
            title: "Marketplace",
            key: "status",
            render: (_, row) => <Status row={row} />,
          },
          {
            title: "Review",
            key: "review",
            render: (_, row) => (
              <Button
                onClick={() => {
                  setSelected(row);
                  setNotice("");
                  setError("");
                }}
              >
                Review application #{row.id}
              </Button>
            ),
          },
        ]}
      />
      <Modal
        open={!!selected}
        onCancel={() => {
          if (!busy) setSelected(null);
        }}
        closable={!busy}
        maskClosable={!busy}
        keyboard={!busy}
        width={1000}
        style={{ top: 24 }}
        styles={{
          body: { maxHeight: "calc(100dvh - 190px)", overflowY: "auto" },
        }}
        title={`Property application #${selected?.id || ""}`}
        footer={[
          <Button key="close" disabled={busy} onClick={() => setSelected(null)}>
            Close review
          </Button>,
          <Button
            key="visibility"
            type="primary"
            danger={currentStatus === "live"}
            loading={saving}
            disabled={!!contentAction}
            onClick={() =>
              changeVisibility(currentStatus === "live" ? "withdrawn" : "live")
            }
          >
            {currentStatus === "live"
              ? "Withdraw from market"
              : "Push to market"}
          </Button>,
        ]}
      >
        {selected && (
          <div className="support-property-review">
            <Status row={selected} />
            <p>
              {currentStatus === "live"
                ? "This application is visible to lenders. Withdraw it to hide it from the marketplace."
                : "This application is hidden from lenders. Review the details and documents before pushing it to market."}
            </p>
            {error && <Alert role="alert" type="error" message={error} />}
            {contentNotice && (
              <Alert role="status" type="success" message={contentNotice} />
            )}
            <h3>Borrower</h3>
            <p>
              <b>{selected.borrower?.principal_name || selected.user_id}</b>
              <br />
              {selected.borrower?.email ||
                selected.borrower?.company_email ||
                "—"}
              <br />
              {selected.borrower?.mobile_no ||
                selected.borrower?.company_contact ||
                "—"}
            </p>
            {selected.borrower && onOpenBorrower && (
              <Button
                disabled={busy}
                onClick={() => onOpenBorrower(selected.borrower)}
              >
                Open borrower profile
              </Button>
            )}
            <h3>Submitted application details</h3>
            <Descriptions
              aria-label="Submitted application details"
              bordered
              size="small"
              column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }}
            >
              {detailRows.map(([label, value]) => (
                <Descriptions.Item key={label} label={label}>
                  {value}
                </Descriptions.Item>
              ))}
            </Descriptions>
            <h3>Co-owners</h3>
            {coOwners.length ? (
              <Table
                size="small"
                pagination={false}
                rowKey={(_, index) => index}
                scroll={{ x: 600 }}
                dataSource={coOwners}
                columns={[
                  { title: "Name", dataIndex: "name" },
                  { title: "Relationship", dataIndex: "relationship" },
                  { title: "Mobile", dataIndex: "phone" },
                  {
                    title: "CPF housing usage",
                    dataIndex: "cpfUsage",
                    render: money,
                  },
                ]}
              />
            ) : (
              <p>No co-owners declared.</p>
            )}
            <PropertyCoOwnerInvitations key={selected.id} application={selected} disabled={busy} />
            <h3>Submitted documents</h3>
            {documents.length ? (
              <ul>
                {documents.map((document, index) => {
                  const url = documentUrl(document.file_url || document.url);
                  return (
                    <li key={index}>
                      <b>{document.category}:</b>{" "}
                      {url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          {document.file_name ||
                            document.name ||
                            document.category}
                        </a>
                      ) : (
                        "File link unavailable"
                      )}
                      {document.removable && document.document_id != null && (
                        <Button
                          className="support-property-document-remove"
                          danger
                          size="small"
                          disabled={busy}
                          loading={
                            contentAction ===
                            `DELETE:documents:${document.document_id}`
                          }
                          aria-label={`Remove additional document ${document.file_name || document.name || index + 1}`}
                          onClick={() =>
                            changeContent(
                              "documents",
                              document.document_id,
                              "DELETE",
                            )
                          }
                        >
                          Remove
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p>No documents attached to this application.</p>
            )}
            <div className="support-property-content-editor">
              <h3>Additional documents for lenders</h3>
              <p>
                These files are included with the application’s existing
                documents when lenders download the loan document ZIP.
              </p>
              <div className="support-property-document-uploads">
                {DOCUMENT_CATEGORIES.map(([category, label]) => {
                  const files = uploadFiles[category] || [];
                  const inputId =
                    category === "other"
                      ? "property-additional-documents"
                      : `property-documents-${category}`;
                  return (
                    <section
                      className="support-property-document-upload"
                      key={category}
                      aria-label={`${label} upload`}
                    >
                      <label htmlFor={inputId}>{label}</label>
                      {category === "bank_statement" && (
                        <p>
                          Bank statements are processed automatically. Lenders
                          see the results in Customer Document Analysis once
                          processing completes.
                        </p>
                      )}
                      <input
                        ref={(element) => {
                          fileInputs.current[category] = element;
                        }}
                        id={inputId}
                        type="file"
                        multiple
                        accept={
                          category === "bank_statement"
                            ? ".pdf,.csv,.xls,.xlsx,.zip"
                            : undefined
                        }
                        disabled={busy}
                        onChange={(event) => {
                          const selectedFiles = Array.from(
                            event.target.files || [],
                          );
                          setUploadFiles((current) => ({
                            ...current,
                            [category]: selectedFiles,
                          }));
                        }}
                      />
                      <p>Up to 10 files per upload, 50 MB each.</p>
                      {files.length > 0 && (
                        <ul>
                          {files.map((file, index) => (
                            <li key={index}>{file.name}</li>
                          ))}
                        </ul>
                      )}
                      <Button
                        loading={contentAction === `documents:${category}`}
                        disabled={busy || !files.length}
                        onClick={() => saveContent("documents", category)}
                      >
                        {category === "other"
                          ? "Upload additional documents"
                          : `Upload ${label}`}
                      </Button>
                    </section>
                  );
                })}
              </div>
              <PropertyApplicationComments
                comments={loan.support_comments || []}
                renderActions={(comment) =>
                  comment.id && (
                    <div className="support-property-comment-actions">
                      {editingComment === comment.id ? (
                        <>
                          <label
                            htmlFor={`edit-property-comment-${comment.id}`}
                          >
                            Edit comment for lenders
                          </label>
                          <textarea
                            id={`edit-property-comment-${comment.id}`}
                            rows={8}
                            maxLength={100000}
                            value={editDraft}
                            disabled={busy}
                            onChange={(event) =>
                              setEditDraft(event.target.value)
                            }
                          />
                          <div className="support-property-comment-count">
                            {editDraft.length.toLocaleString("en-SG")} / 100,000
                            characters
                          </div>
                          <Button
                            type="primary"
                            disabled={busy || !editDraft.trim()}
                            loading={
                              contentAction === `PATCH:comments:${comment.id}`
                            }
                            onClick={() =>
                              changeContent("comments", comment.id, "PATCH")
                            }
                          >
                            Save comment
                          </Button>
                          <Button
                            disabled={busy}
                            onClick={() => {
                              setEditingComment(null);
                              setEditDraft("");
                            }}
                          >
                            Cancel edit
                          </Button>
                        </>
                      ) : (
                        <Button
                          disabled={busy || editingComment !== null}
                          onClick={() => {
                            setEditingComment(comment.id);
                            setEditDraft(comment.body);
                          }}
                        >
                          Edit comment
                        </Button>
                      )}
                      <Button
                        danger
                        disabled={busy}
                        loading={
                          contentAction === `DELETE:comments:${comment.id}`
                        }
                        onClick={() =>
                          changeContent("comments", comment.id, "DELETE")
                        }
                      >
                        Delete comment
                      </Button>
                    </div>
                  )
                }
              />
              <h3>Add a comment for lenders</h3>
              <p>
                Saved comments are visible to lenders in this application’s
                borrower profile.
              </p>
              <label htmlFor="property-lender-comment">
                Comment for lenders
              </label>
              <textarea
                id="property-lender-comment"
                rows={8}
                maxLength={100000}
                value={commentDraft}
                disabled={busy}
                onChange={(event) => setCommentDraft(event.target.value)}
              />
              <div className="support-property-comment-count">
                {commentDraft.length.toLocaleString("en-SG")} / 100,000
                characters
              </div>
              <Button
                type="primary"
                loading={contentAction === "comments"}
                disabled={busy || !commentDraft.trim()}
                onClick={() => saveContent("comments")}
              >
                Add lender comment
              </Button>
            </div>
            {loan.cbs_report_document?.extracted_data && (
              <details>
                <summary>CBS extracted details</summary>
                <pre>
                  {JSON.stringify(
                    loan.cbs_report_document.extracted_data,
                    null,
                    2,
                  )}
                </pre>
              </details>
            )}
            <h3>Requested repayment schedule</h3>
            <PropertyLoanRepaymentSchedule
              schedule={loan.loanRepaymentSchedule || []}
            />
          </div>
        )}
      </Modal>
    </section>
  );
}
